import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { formatCount, formatNumber, formatTaka } from '@/utils/number';
import type { PcmpRowSelection } from '../../hooks/usePcmpRowSelection';

interface PcmpSelectionBarProps {
  selection: PcmpRowSelection;
  /** The selected count and amount (from `selection.summarize`). */
  summary: { count: number; amount: number };
  /** Offer "select all matching" once the whole page is ticked. */
  selectAll?: { total: number; amount: number; isPageSelected: boolean } | undefined;
  hint: string;
  /** Show the selected amount; off where the list has no matching total. */
  withAmount?: boolean;
  children: ReactNode;
}

/** The bar above a selectable list: what is picked, "select all matching", and the action. */
export function PcmpSelectionBar({
  selection,
  summary,
  selectAll,
  hint,
  withAmount = true,
  children,
}: PcmpSelectionBarProps) {
  const hasSelection = summary.count > 0;
  const canSelectAll =
    selectAll !== undefined &&
    !selection.allMatching &&
    selectAll.isPageSelected &&
    selectAll.total > summary.count;

  return (
    <div
      className="flex flex-col gap-2 rounded-xl border bg-card px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {hasSelection ? (
          <>
            <span className="font-medium">
              {selection.allMatching ? 'All matching: ' : ''}
              {formatCount(summary.count, 'proposal')} selected
              {withAmount ? ` · ${formatTaka(summary.amount)}` : ''}
            </span>
            {canSelectAll ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={selection.selectAllMatching}
              >
                Select all {formatNumber(selectAll.total)} matching
                {withAmount ? ` (${formatTaka(selectAll.amount)})` : ''}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-muted-foreground"
              onClick={selection.clear}
            >
              Clear
            </Button>
          </>
        ) : (
          <span className="text-muted-foreground">{hint}</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">{children}</div>
    </div>
  );
}
